import type { ClientPackage } from '@salonomy/types';
import { Button, Modal, Pill } from '@salonomy/ui';
import { useClientPackages } from '../../data/packages.js';
import { money } from '../../lib/format.js';
import './till.css';

const STATUS_TONE: Record<ClientPackage['status'], 'success' | 'neutral' | 'danger'> = {
  active: 'success',
  expired: 'neutral',
  void: 'danger',
};

/**
 * WS-13 Feature 3a — staff/till package BALANCE lookup, mirroring VoucherBalanceModal. Shows the SERVER's
 * DERIVED remaining sessions/value for each of a client's purchased packages. Read-only: this never
 * redeems and never touches money — it DISPLAYS the API's `remainingSessions` / `remainingValuePence`
 * (which are the signed sum of the ledger, never stored), so what's shown is always the ledger truth.
 * Redemption lives in RedeemPackageModal; this is the "what's left on this client's packages?" question.
 *
 * Keyed on the basket's client (a package is a per-client instrument, not a code), so it's gated on a
 * clientId — a walk-in checkout has no packages to show.
 */
export function PackageBalanceModal({
  clientId,
  clientName,
  onClose,
}: {
  clientId: string | null;
  clientName?: string | null;
  onClose: () => void;
}) {
  const { data: packages, isFetching, isError } = useClientPackages(clientId);

  return (
    <Modal
      open
      onClose={onClose}
      title="Package balance"
      width={460}
      footer={
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      }
    >
      {clientId == null ? (
        <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>
          This checkout has no client on it, so there are no purchased packages to show. Add a
          client to the booking first.
        </div>
      ) : isError ? (
        <div className="muted" style={{ fontSize: 13.5 }}>
          Couldn&rsquo;t load this client&rsquo;s packages. Please try again.
        </div>
      ) : isFetching && (packages?.length ?? 0) === 0 ? (
        <div className="muted" style={{ fontSize: 13.5 }}>
          Loading packages…
        </div>
      ) : (packages?.length ?? 0) === 0 ? (
        <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>
          {clientName ? `${clientName} has` : 'This client has'} no purchased packages.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(packages ?? []).map((p) => (
            <div key={p.id} className="till-voucher__found">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600 }}>{p.name}</span>
                <Pill tone={STATUS_TONE[p.status]}>{p.status}</Pill>
              </div>
              {p.remainingSessions != null ? (
                <div className="till__line">
                  <span>Sessions remaining</span>
                  <span className="mono">
                    {p.remainingSessions}
                    {p.originalSessions != null ? ` of ${p.originalSessions}` : ''}
                  </span>
                </div>
              ) : null}
              <div className="till__line till__line--total">
                <span>Value remaining</span>
                <span className="mono">{money(p.remainingValuePence)}</span>
              </div>
              <div className="till__line muted">
                <span>Original value</span>
                <span className="mono">{money(p.originalAmountPence)}</span>
              </div>
              {p.expiresAt ? (
                <div className="muted" style={{ fontSize: 12.5 }}>
                  Expires {p.expiresAt.slice(0, 10)}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
